import { registerMutation, registerQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import getLangLocale from 'I18n/utils/getLangLocale';
import { getSupportedLocaleForPropertiesV4 } from 'automation-ui-management/utils/locale';
import { AutomationPlatformFlowProperties } from 'automation-ui-management/constants/CrmWorkflowProperties';
import { AUTOMATION_PLATFORM_FLOW_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
export const CRM_OBJECT_SEARCH_FIELD_NAME = 'automation_ui_index_crmObjectSearch';
export const CRM_OBJECT_SEARCH_BY_FLOW_ID = registerQuery({
  fieldName: CRM_OBJECT_SEARCH_FIELD_NAME,
  args: ['flowId'],
  fetcher: ({
    flowId
  }) => {
    return http.get(`automationapps/v1/flow-crm-objects/${flowId}`);
  }
});

/**
 * Query for fetching crm properties.
 */
export const CRM_OBJECT_PROPERTIES_FIELD_NAME = 'automation_ui_index_crmObjectProperties';
export const GET_OBJECT_PROPERTIES = registerQuery({
  fieldName: CRM_OBJECT_PROPERTIES_FIELD_NAME,
  args: ['objectTypeId'],
  fetcher({
    objectTypeId
  }) {
    const endpointLocale = getSupportedLocaleForPropertiesV4(getLangLocale());
    return http.get(`properties/v4/groups/${objectTypeId}?includeProperties=true`, {
      query: {
        locale: endpointLocale
      }
    }).then(({
      results
    }) => results && results.length ? results[0] : null);
  }
});
export const GET_CRM_OBJECT_PROPERTIES = registerQuery({
  fieldName: 'getCrmObjectProperties',
  args: ['objectTypeId'],
  fetcher({
    objectTypeId
  }) {
    const endpointLocale = getSupportedLocaleForPropertiesV4(getLangLocale());
    return http.get(`properties/v4/${objectTypeId}`, {
      query: {
        locale: endpointLocale
      }
    });
  }
});
export const GET_CRM_OBJECT_PROPERTY = registerQuery({
  fieldName: 'getCrmObjectProperty',
  args: ['objectTypeId', 'propertyName'],
  fetcher({
    objectTypeId,
    propertyName
  }) {
    const endpointLocale = getSupportedLocaleForPropertiesV4(getLangLocale());
    return http.get(`properties/v4/${objectTypeId}/named/${propertyName}`, {
      query: {
        locale: endpointLocale
      }
    }).catch(err => {
      if (err.status === 404) {
        return null;
      }
      throw err;
    });
  }
});
export const CRM_SEARCH_FIELD_NAME = 'automation_ui_index_crmSearch';
export const CRM_SEARCH = registerQuery({
  fieldName: CRM_SEARCH_FIELD_NAME,
  args: ['crmSearchOptions'],
  fetcher: ({
    crmSearchOptions
  }) => {
    return http.post(`crm-search/search`, {
      data: crmSearchOptions
    });
  }
});
export const UPDATE_CRM_OBJECTS_BUSINESS_UNIT_FIELD_NAME = 'automation_ui_management_updateCrmObjectsBusinessUnit';
export const UPDATE_CRM_OBJECTS_BUSINESS_UNIT = registerMutation({
  fieldName: UPDATE_CRM_OBJECTS_BUSINESS_UNIT_FIELD_NAME,
  args: ['changes', 'objectTypeId'],
  fetcher({
    changes,
    objectTypeId
  }) {
    return http.put(`inbounddb-objects/v1/crm-objects/${objectTypeId}`, {
      data: Object.keys(changes).map(objectId => ({
        objectId,
        propertyValues: [{
          name: AutomationPlatformFlowProperties.BUSINESS_UNIT,
          value: changes[objectId]
        }]
      }))
    });
  }
});

// Dev Note: Keeping this generic to avoid duplicate api calls
export const WORKFLOW_COUNT_FOR_LIMIT_FIELDNAME = 'workflowCountForLimit';
export const GET_WORKFLOW_COUNT_FOR_LIMIT = registerQuery({
  fieldName: WORKFLOW_COUNT_FOR_LIMIT_FIELDNAME,
  fetcher: () => {
    const data = {
      objectTypeId: AUTOMATION_PLATFORM_FLOW_ID,
      count: 0,
      filterGroups: [{
        filters: [{
          operator: 'EQ',
          value: 'true',
          property: 'hs_is_counted_towards_hub_limits_usage'
        }, {
          operator: 'EQ',
          value: 'false',
          property: 'hs_is_deleted'
        }, {
          operator: 'IN',
          values: ['WORKFLOWS_APP', 'WORKFLOWS_CLASSIC', 'DIRECT_API'],
          property: 'hs_source_app'
        }]
      }]
    };
    return http.post(`crm-search/search`, {
      data
    }).then(response => response.total || 0);
  }
});

// Dev Note: The NewPropertiesForm does not play well with the crmObjectQuery
// Firstly is that it return all associated properties including hubspot defined ones
// Had to fitler all those out with exceptions. I also had to format the results to a key value pair
export const GET_FILTERED_PROPERTIES_QUERY_FIELDNAME = 'automation_ui_index_filteredCrmObjectProperties';
export const GET_FILTERED_PROPERTIES_QUERY = registerQuery({
  fieldName: GET_FILTERED_PROPERTIES_QUERY_FIELDNAME,
  args: ['flowId'],
  fetcher: ({
    flowId
  }) => {
    const data = {
      objectTypeId: AUTOMATION_PLATFORM_FLOW_ID,
      count: 1,
      offset: 0,
      query: '',
      requestOptions: {
        allPropertiesFetchMode: 'latest_version',
        includeAllProperties: true
      },
      filterGroups: [{
        filters: [{
          operator: 'EQ',
          value: flowId,
          property: 'hs_flow_id'
        }]
      }]
    };
    return http.post(`crm-search/search`, {
      data
    }).then(({
      results
    }) => Object.keys(results[0].properties).filter(key =>
    // Dev note: Filtering out hubspot defined properties that we don't need
    // confirmed that users cannot create properties with hs_
    !key.includes('hs_') || key === 'hs_all_assigned_business_unit_ids').reduce((result, property) => {
      result[property] = results[0].properties[property].value;
      return result;
    }, {}));
  }
});
export const UPDATE_CRM_OBJECT_FIELD_NAME = 'automation_ui_management_updateCrmObjects';
export const UPDATE_CRM_OBJECT = registerMutation({
  fieldName: UPDATE_CRM_OBJECT_FIELD_NAME,
  args: ['properties', 'objectTypeId', 'objectId'],
  fetcher({
    properties,
    objectTypeId,
    objectId
  }) {
    return http.put(`inbounddb-objects/v1/crm-objects/${objectTypeId}/${objectId}`, {
      data: properties
    });
  }
});
async function fetchFlowFromFilter({
  view,
  offset = 0
}) {
  const response = await http.post(`crm-search/search`, {
    data: {
      offset,
      objectTypeId: '0-44',
      filterGroups: view.filterGroups,
      count: 500,
      requestOptions: {
        properties: [AutomationPlatformFlowProperties.OBJECT_ID]
      }
    }
  });
  if (response.hasMore) {
    const nextPage = await fetchFlowFromFilter({
      offset: response.offset,
      view
    });
    return {
      results: [...response.results, ...nextPage.results],
      hasMore: nextPage.hasMore,
      offset: nextPage.offset,
      total: response.total
    };
  }
  return response;
}
export function getFlowsFromFilters({
  selectedViews
}) {
  const selectedViewsResponseArr = selectedViews.map(view => {
    return fetchFlowFromFilter({
      view
    }).then(({
      results
    }) => {
      return {
        viewId: view.viewId,
        flowCrmObjectIds: results.map(flow => flow.properties.hs_object_id.value)
      };
    });
  });
  return Promise.all(selectedViewsResponseArr).catch(e => console.log(e));
}